/**
 * The proof's pure parts (docs/storybook-identity-lock.md §9): which pictures
 * the parent checks, what a redraw round sends, and the zoom arithmetic of
 * the large view. StorybookProof.tsx draws them.
 */

import {
  storybookMediaUrl,
  type StorybookBook,
  type StorybookProofReason,
  type StorybookProofRedrawRequest,
} from '@/lib/storybook-api';

export type StorybookProofVerdict = 'ok' | 'redraw';

export interface StorybookProofChoice {
  verdict: StorybookProofVerdict;
  /** Only with 'redraw', and only when the parent picked one. */
  reason?: StorybookProofReason;
}

/** The parent's answer per picture key; a picture with no entry is not checked yet. */
export type StorybookProofChoices = Partial<Record<string, StorybookProofChoice>>;

export interface StorybookProofItem {
  key: string;
  /** 0 = the cover, else the page number (1..10). */
  page: number;
  image: string | null;
  text: string | null;
}

export const STORYBOOK_PROOF_REASONS: StorybookProofReason[] = ['face', 'wrong_person', 'other'];
/** A flagged picture with no reason picked is redrawn as a face that doesn't look right (stronger likeness). */
export const DEFAULT_PROOF_REASON: StorybookProofReason = 'face';
/** When an older answer leaves maxRounds out (STORYBOOK_MAX_PROOF_ROUNDS' default). */
export const DEFAULT_PROOF_ROUNDS = 3;

/** 'cover' -> 0, 'page03' -> 3, anything else -> null. */
export function proofPageNumber(key: string): number | null {
  if (key === 'cover') return 0;
  const match = /^page(\d{1,2})$/.exec(key);
  const n = match ? Number(match[1]) : 0;
  return n >= 1 ? n : null;
}

const pageKey = (index: number) => `page${String(index + 1).padStart(2, '0')}`;

/**
 * The pictures to check, in book order: the proof's own keys, pictures and
 * words, each falling back to the book's cover and pages when the proof
 * leaves one out.
 */
export function proofItems(book: StorybookBook): StorybookProofItem[] {
  const proof = book.proof || {};
  const pages = book.pages || [];
  const keys = proof.keys?.length ? proof.keys : ['cover', ...pages.map((page) => pageKey(page.index))];
  const items: StorybookProofItem[] = [];
  for (const key of keys) {
    const page = proofPageNumber(key);
    if (page == null) continue;
    const bookPage = page > 0 ? pages.find((entry) => entry.index === page - 1) : undefined;
    const image = proof.images?.[key] ?? (page === 0 ? book.coverUrl : bookPage?.imageUrl) ?? null;
    const text = page === 0 ? null : proof.texts?.[key] ?? bookPage?.text ?? null;
    items.push({ key, page, image: storybookMediaUrl(image), text: text || null });
  }
  return items;
}

/**
 * POST /proof/redraw's body: the pictures marked "Redraw this one", in book
 * order, each with its reason. The reason chips are optional; a picture
 * flagged without one goes as DEFAULT_PROOF_REASON, as the server records it.
 */
export function proofRedrawRequest(items: StorybookProofItem[], choices: StorybookProofChoices): StorybookProofRedrawRequest {
  const keys = items.filter((item) => choices[item.key]?.verdict === 'redraw').map((item) => item.key);
  const reasons: StorybookProofRedrawRequest['reasons'] = {};
  for (const key of keys) reasons[key] = choices[key]?.reason ?? DEFAULT_PROOF_REASON;
  return { keys, reasons };
}

// ---------------------------------------------------------------------------
// Zoom. A translate + scale on the picture (origin top-left), clamped so the
// picture always covers the frame: tap to zoom in on a face, drag to move,
// pinch on a phone, or the buttons / + - 0 and the arrow keys.
// ---------------------------------------------------------------------------

export interface StorybookZoomView {
  s: number;
  tx: number;
  ty: number;
}

export const ZOOM_FIT: StorybookZoomView = { s: 1, tx: 0, ty: 0 };
export const ZOOM_MAX = 4;
/** A tap zooms this far in on the spot tapped (a face). */
export const ZOOM_TAP = 2.5;
/** One zoom button press or +/- key. */
export const ZOOM_STEP = 1.5;

/** Keeps the zoom in 1..ZOOM_MAX and the picture covering the w x h frame. */
export function clampZoom(view: StorybookZoomView, w: number, h: number): StorybookZoomView {
  const s = Math.min(ZOOM_MAX, Math.max(1, view.s));
  if (s <= 1.001) return ZOOM_FIT;
  return {
    s,
    tx: Math.min(0, Math.max(w * (1 - s), view.tx)),
    ty: Math.min(0, Math.max(h * (1 - s), view.ty)),
  };
}

/** Zooms to `s`, keeping the frame point (qx, qy) over the same spot of the picture. */
export function zoomAround(view: StorybookZoomView, s: number, qx: number, qy: number, w: number, h: number): StorybookZoomView {
  const next = Math.min(ZOOM_MAX, Math.max(1, s));
  const px = (qx - view.tx) / view.s;
  const py = (qy - view.ty) / view.s;
  return clampZoom({ s: next, tx: qx - next * px, ty: qy - next * py }, w, h);
}
